/**
 * BlueNoiseGenerator — CPU void-and-cluster (Ulichney 1993) blue-noise
 * texture generator.
 *
 * Generates a tiling 2D blue-noise pattern: a binary array where on-pixels
 * are spread maximally far apart, then ranked progressively. The output
 * is a Uint8 buffer where each value `[0, 255]` is the rank of the texel
 * in the spread sequence — the high-frequency-only spectrum that gives
 * blue noise its visually flat appearance.
 *
 * Algorithm summary:
 *   1. Random seed, ~10% on-pixels.
 *   2. Tighten: find the tightest cluster (highest-energy on-pixel) and
 *      the largest void (lowest-energy off-pixel). Swap. Repeat until
 *      no improvement. (The "energy" is a Gaussian-filtered density map.)
 *   3. Phase 1 (rank low): remove on-pixels one at a time by finding the
 *      tightest cluster; record the rank at which each was removed.
 *   4. Phase 2 (rank mid): re-add on-pixels at the largest void, recording
 *      ranks above the initial count.
 *   5. Phase 3 (rank high): continue past 50% to fully rank all texels.
 *   6. Output `rank / (N² - 1)` → [0, 255] in 8-bit storage.
 *
 * For a 64² texture (4096 texels), generation takes ~100 ms. We use a
 * smaller sigma (≈1.5 px) and a fixed FNV-hashed seed so output is
 * deterministic across runs.
 *
 * For RGBA8 output, we run the algorithm 4 times with independent seeds.
 * Each channel becomes a decorrelated noise sequence.
 */

const FILTER_SIGMA = 1.9;
const FILTER_RADIUS = 4; // 9×9 kernel (cuts to <0.001 contribution at sigma 1.9)

/**
 * Generate a void-and-cluster blue-noise pattern of size NxN.
 *
 * @param size  texture side length (power of 2 recommended)
 * @param seed  PRNG seed for reproducibility (default 0xCAFE)
 * @returns     Uint8Array of length N*N with values in [0, 255]
 */
export function generateBlueNoise(size: number, seed = 0xcafe): Uint8Array {
  const N = size * size;
  const initialOnCount = Math.max(1, Math.floor(N * 0.1));

  // Reproducible PRNG (mulberry32).
  let state = seed >>> 0;
  const rand = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (((t ^ (t >>> 14)) >>> 0) / 4294967296);
  };

  // Initial random binary pattern.
  const onPixels = new Uint8Array(N);
  let onCount = 0;
  while (onCount < initialOnCount) {
    const idx = Math.floor(rand() * N);
    if (!onPixels[idx]) {
      onPixels[idx] = 1;
      onCount++;
    }
  }

  // Energy buffer (Gaussian-weighted density of on-pixels).
  const energy = new Float32Array(N);
  _recomputeEnergy(onPixels, energy, size);

  // Phase 0 — tighten the initial pattern.
  for (let iter = 0; iter < 5000; iter++) {
    const tightest = _findTightestCluster(onPixels, energy);
    if (tightest < 0) break;
    onPixels[tightest] = 0;
    _addEnergyContribution(energy, size, tightest, -1);

    const largest = _findLargestVoid(onPixels, energy);
    if (largest < 0) {
      onPixels[tightest] = 1;
      _addEnergyContribution(energy, size, tightest, 1);
      break;
    }
    onPixels[largest] = 1;
    _addEnergyContribution(energy, size, largest, 1);

    if (largest === tightest) break;
  }

  // Output rank buffer.
  const ranks = new Int32Array(N);
  for (let i = 0; i < N; i++) ranks[i] = -1;

  // Phase 1: rank from N_init down to 0.
  // Make a working copy of onPixels; remove tightest at a time.
  const work = new Uint8Array(onPixels);
  let workCount = onCount;
  const workEnergy = new Float32Array(energy);
  for (let rank = workCount - 1; rank >= 0; rank--) {
    const t = _findTightestCluster(work, workEnergy);
    if (t < 0) break;
    ranks[t] = rank;
    work[t] = 0;
    _addEnergyContribution(workEnergy, size, t, -1);
    workCount--;
  }

  // Phase 2 + 3: rank from N_init up to N-1 by adding into largest voids.
  // Reset work to the post-Phase-0 onPixels.
  work.set(onPixels);
  workEnergy.set(energy);
  workCount = onCount;
  for (let rank = onCount; rank < N; rank++) {
    const v = _findLargestVoid(work, workEnergy);
    if (v < 0) break;
    ranks[v] = rank;
    work[v] = 1;
    _addEnergyContribution(workEnergy, size, v, 1);
    workCount++;
  }

  // Backfill any missing ranks (shouldn't happen but be defensive).
  // Use a Set for O(1) "is this rank already taken" lookups so the backfill
  // is O(N) rather than O(N²). Cap `next` at N-1 so a runaway inner loop
  // can never write out-of-range rank values that would overflow to 255
  // in the final 8-bit conversion.
  const taken = new Set<number>();
  for (let i = 0; i < N; i++) {
    if (ranks[i] >= 0) taken.add(ranks[i]);
  }
  let next = 0;
  for (let i = 0; i < N; i++) {
    if (ranks[i] < 0) {
      while (next < N && taken.has(next)) next++;
      if (next >= N) break;
      ranks[i] = next;
      taken.add(next);
      next++;
    }
  }

  // Convert ranks to 8-bit.
  const out = new Uint8Array(N);
  const denom = Math.max(1, N - 1);
  for (let i = 0; i < N; i++) {
    out[i] = Math.round((ranks[i] / denom) * 255);
  }
  return out;
}

/**
 * Generate a 4-channel (RGBA) blue-noise pattern. Each channel is an
 * independent void-and-cluster sequence with a different seed.
 */
export function generateBlueNoiseRGBA(
  size: number, baseSeed = 0xcafe,
): Uint8Array {
  const N = size * size;
  const out = new Uint8Array(N * 4);
  for (let ch = 0; ch < 4; ch++) {
    const channel = generateBlueNoise(size, baseSeed + ch * 0x9e3779b1);
    for (let i = 0; i < N; i++) out[i * 4 + ch] = channel[i];
  }
  return out;
}

// ─── Internals ──────────────────────────────────────────────────────────────

const _gaussianCache = new Map<number, Float32Array>();
function _gaussianKernel(sigma: number, radius: number): Float32Array {
  const key = sigma * 1000 + radius;
  const cached = _gaussianCache.get(key);
  if (cached) return cached;
  const w = radius * 2 + 1;
  const k = new Float32Array(w * w);
  const inv2sigma2 = 1 / (2 * sigma * sigma);
  let sum = 0;
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      const v = Math.exp(-(dx * dx + dy * dy) * inv2sigma2);
      k[(dy + radius) * w + (dx + radius)] = v;
      sum += v;
    }
  }
  for (let i = 0; i < k.length; i++) k[i] /= sum;
  _gaussianCache.set(key, k);
  return k;
}

function _recomputeEnergy(
  onPixels: Uint8Array, out: Float32Array, size: number,
): void {
  out.fill(0);
  for (let i = 0; i < onPixels.length; i++) {
    if (onPixels[i]) _addEnergyContribution(out, size, i, 1);
  }
}

function _addEnergyContribution(
  energy: Float32Array, size: number, idx: number, sign: number,
): void {
  const kernel = _gaussianKernel(FILTER_SIGMA, FILTER_RADIUS);
  const r = FILTER_RADIUS;
  const w = r * 2 + 1;
  const px = idx % size;
  const py = Math.floor(idx / size);
  for (let dy = -r; dy <= r; dy++) {
    const ty = ((py + dy) % size + size) % size; // toroidal wrap
    for (let dx = -r; dx <= r; dx++) {
      const tx = ((px + dx) % size + size) % size;
      const k = kernel[(dy + r) * w + (dx + r)];
      energy[ty * size + tx] += sign * k;
    }
  }
}

function _findTightestCluster(
  onPixels: Uint8Array, energy: Float32Array,
): number {
  let best = -1;
  let bestE = -Infinity;
  for (let i = 0; i < onPixels.length; i++) {
    if (!onPixels[i]) continue;
    if (energy[i] > bestE) { bestE = energy[i]; best = i; }
  }
  return best;
}

function _findLargestVoid(
  onPixels: Uint8Array, energy: Float32Array,
): number {
  let best = -1;
  let bestE = Infinity;
  for (let i = 0; i < onPixels.length; i++) {
    if (onPixels[i]) continue;
    if (energy[i] < bestE) { bestE = energy[i]; best = i; }
  }
  return best;
}

// `_hasRank` was a linear scan used by the old O(N²) backfill loop.
// Replaced by a Set-based lookup (see _voidAndCluster); function kept
// only as the original is no longer reachable.
